import { createContext, useState, useEffect } from "react";
import { NAME_SOUND_ACTIVE_BANK, useGameSounds, useMediaQuery, useTranslate } from "@educaplay/core";
import { Fill } from "@educaplay/core/components";
import DialogClueContent from '../DialogClueContent';
import DialogClueHeader from '../DialogClueHeader';
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { VARIANT_ICON_BUTTON } from "../../utils/constants";
import DialogClue from "../DialogClue";
import classes from './CluesProvider.module.scss';
import clsx from 'clsx';
import { useDispatch } from "react-redux";
import { setNegativePoints } from "@educaplay/store/slices/gameSlice";

export const CluesContext = createContext();

const formatClues = (clues) => {
    return clues.map((clue, index) => ({
        ...clue,
        requested: false,
        id: `${index + 1}`
    }));
}

const LEVELS_OF_PIXELATION = 3;
const MIN_LEVEL_PIXELATION = 0;
const INITIAL_LEVEL_PIXELATION = 3;
const DELAY_SOUND_CLUE = 300;

export default function CluesProvider({ children, lettersAnswer, revealLetter, inputRef }) {
    const clues = useSelector(state => state.game.questions[state.game.currentIndex].clues);
    const currentQuestion = useSelector(state => state.game.questions[state.game.currentIndex]);
    const hasBank = useSelector(state => state.game.hasBank);
    const gameSounds = useGameSounds();
    const isCompleteEntryAnimation = useSelector(state => state.game.isCompleteEntryAnimation);
    const isAnimating = useSelector(state => state.game.isAnimating);
    const hasClues = clues.length > 0;
    const dispatch = useDispatch();
    const pointsLose = useSelector(state => state.game.negativePoints);


    const [formattedClues, setFormattedClues] = useState(formatClues(clues));
    const [selectedClue, setSelectedClue] = useState(null);
    const [lastClueViewed, setLastClueViewed] = useState(null);
    const [pixelatedLevel, setPixelatedLevel] = useState(currentQuestion.revealImage ? INITIAL_LEVEL_PIXELATION : MIN_LEVEL_PIXELATION);
    const [numExtraClue, setNumExtraClue] = useState(0);
    const [hasRequestedClue, setHasRequestedClue] = useState(false);
    const [currentIconButton, setCurrentIconButton] = useState(
        hasClues > 0 ? VARIANT_ICON_BUTTON.INFORMATION : currentQuestion.revealImage ? VARIANT_ICON_BUTTON.IMAGE : VARIANT_ICON_BUTTON.LETTERS
    );

    const requestedClues = formattedClues.filter(clue => clue.requested);
    const numRequestedClues = requestedClues.length + numExtraClue;
    const numUserClues = clues.length;
    const numImageClues = currentQuestion.revealImage ? LEVELS_OF_PIXELATION : 0;
    const numLettersClues = currentQuestion.revealLetters ? lettersAnswer.flat().filter(letter => !letter.disabled).length - 1 : 0;
    const remainingLetters = numRequestedClues - numUserClues - numImageClues;
    const numClues = numUserClues + numImageClues + numLettersClues + (remainingLetters > 0 ? remainingLetters : 0);
    const isTouchDevice = (useMediaQuery("(hover:none)") || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0));
    const showDialogClue = hasClues && hasRequestedClue;

    const clueCost = (() => {
        if (pointsLose >= currentQuestion.points) return 0;

        const numRealLettersClue = numLettersClues + (remainingLetters > 0 ? remainingLetters : 0);
        if (numUserClues > 0 && numImageClues > 0 && numLettersClues > 0) {
            if (numRequestedClues < numUserClues) {
                return (currentQuestion.points * 0.2) / numUserClues;
            }
            if (numRequestedClues < numImageClues + numUserClues) {
                return (currentQuestion.points * 0.2) / numImageClues;
            }
            return currentQuestion.points / numRealLettersClue;
        }
        if (numImageClues > 0 && numLettersClues > 0) {
            if (numRequestedClues < numImageClues) {
                return (currentQuestion.points * 0.25) / numImageClues;
            }
            return currentQuestion.points / numRealLettersClue;
        }
        if (numUserClues > 0 && numImageClues > 0) {
            if (numRequestedClues < numUserClues) {
                return (currentQuestion.points * 0.25) / numUserClues;
            }
            return (currentQuestion.points * 0.25) / numImageClues;
        }
        if (numUserClues > 0 && numLettersClues > 0) {
            if (numRequestedClues < numUserClues) {
                return (currentQuestion.points * 0.25) / numUserClues;
            }
            return currentQuestion.points / numRealLettersClue;
        }
        if (numUserClues > 0) {
            return (currentQuestion.points * 0.5) / numUserClues;
        }
        if (numImageClues > 0) {
            return (currentQuestion.points * 0.5) / numImageClues;
        }
        if (numLettersClues > 0) {
            return currentQuestion.points / numRealLettersClue;
        }
        return 0;
    })();

    useEffect(() => {
        setFormattedClues(formatClues(clues));
        setSelectedClue(null);
    }, [clues]);

    useEffect(() => {
        setCurrentIconButton(
            hasClues > 0 ? VARIANT_ICON_BUTTON.INFORMATION : currentQuestion.revealImage ? VARIANT_ICON_BUTTON.IMAGE : VARIANT_ICON_BUTTON.LETTERS
        );
        setPixelatedLevel(currentQuestion.revealImage ? INITIAL_LEVEL_PIXELATION : MIN_LEVEL_PIXELATION);
        setNumExtraClue(0);
        dispatch(setNegativePoints(null));
        setHasRequestedClue(false);
        setLastClueViewed(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion.id])

    const t = useTranslate();

    const requesteNextClue = () => {
        setHasRequestedClue(true);

        const nextClue = formattedClues.find(clue => !clue.requested);
        const negativePointsValue = Math.min(pointsLose + clueCost, currentQuestion.points);

        if (!nextClue) {
            if (currentQuestion.revealImage && pixelatedLevel > MIN_LEVEL_PIXELATION) {
                if (isAnimating) return;
                setPixelatedLevel(pixelatedLevel - 1);
                setNumExtraClue(numExtraClue + 1);
                dispatch(setNegativePoints(negativePointsValue));
                gameSounds.play("clueimage");
                if (pixelatedLevel - 1 === MIN_LEVEL_PIXELATION) setCurrentIconButton(VARIANT_ICON_BUTTON.LETTERS);
                return;
            }
            const emptyLetters = lettersAnswer.flat().filter(letter => !letter.disabled);
            if (currentQuestion.revealLetters && emptyLetters.length > 1) {
                revealLetter();
                if (isAnimating) return;
                gameSounds.play("clue");
                setNumExtraClue(numExtraClue + 1);
                dispatch(setNegativePoints(negativePointsValue));
            }
            return;
        }

        const requestedClue = {
            ...nextClue,
            requested: true
        }
        dispatch(setNegativePoints(negativePointsValue));
        setSelectedClue(requestedClue);
        setLastClueViewed(requestedClue);
        const actualizedFormattedClues = formattedClues.map(clue => {
            if (clue.id === requestedClue.id) {
                return requestedClue;
            }
            return clue;
        });
        gameSounds.play("clue");
        if (!isTouchDevice) inputRef.current.focusFirstLetterEmpty();
        setFormattedClues(actualizedFormattedClues);
        const allCluesRequested = !actualizedFormattedClues.find(clue => !clue.requested);
        if (allCluesRequested) setCurrentIconButton(currentQuestion.revealImage ? VARIANT_ICON_BUTTON.IMAGE : VARIANT_ICON_BUTTON.LETTERS);
    }

    const emptyLetters = lettersAnswer.flat().filter(letter => !letter.disabled);
    const disabledRequestClue = emptyLetters.length < 1 || numClues <= 0 || numClues === numRequestedClues

    useEffect(() => {
        if (disabledRequestClue && numClues > 0) {
            gameSounds.play('move');
        }
    }, [disabledRequestClue, gameSounds, numClues])


    const onCloseHandler = async () => {
        setSelectedClue(null);
    }


    useEffect(() => {
        setTimeout(() => {
            if (isCompleteEntryAnimation) gameSounds.play(NAME_SOUND_ACTIVE_BANK);
        }, selectedClue ? DELAY_SOUND_CLUE : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClue, gameSounds])

    return (
        <CluesContext.Provider value={{
            requestedClues, clues, requesteNextClue, clueCost,
            disabledRequestClue, pixelatedLevel, currentIconButton, numClues, numRequestedClues,
            pointsLose
        }}>

            <Fill name={"core-bank-right"}>
                {t("specific.clues")}: {numRequestedClues}/{numClues}
            </Fill>

            {children}

            {showDialogClue && (
                <DialogClue
                    open={!!selectedClue}
                    onClose={onCloseHandler}
                    onOpen={() => setSelectedClue(lastClueViewed)}
                    className={!hasBank ? classes.adjusttop : null}
                >
                    <DialogClueHeader
                        requestedClues={requestedClues}
                        currentClue={selectedClue}
                        onClueClick={clue => {
                            setSelectedClue(clue);
                            setLastClueViewed(clue);
                        }}
                    />

                    <div
                        className={clsx(classes.portraitdisposition, classes.scrollbar)}
                    >
                        <DialogClueContent
                            mediaType={selectedClue?.type}
                            media={selectedClue?.source}
                            title={t('specific.clue')}
                            explanation={selectedClue?.text}
                        />
                    </div>
                </DialogClue>
            )}
        </CluesContext.Provider>
    );
}

CluesProvider.propTypes = {
    lettersAnswer: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
    children: PropTypes.node,
}